import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Image, X, Lock, Globe, ExternalLink, Users, Phone, MessageSquare, ChevronDown, ChevronUp, Trash2, Upload, FileText } from "lucide-react";
import DateTimePicker from "./DateTimePicker";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper: extract URLs from text
const extractUrls = (text) => {
  if (!text) return [];
  const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/gi;
  return [...new Set(text.match(urlRegex) || [])];
};

// Helper: get platform info for a URL
const getPlatform = (url) => {
  const l = url.toLowerCase();
  if (l.includes("facebook.com") || l.includes("fb.com")) {
    return { name: "Facebook", color: "bg-[#1877F2] hover:bg-[#166ae0]", icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> };
  } else if (l.includes("youtube.com") || l.includes("youtu.be")) {
    return { name: "YouTube", color: "bg-[#FF0000] hover:bg-[#e60000]", icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg> };
  } else if (l.includes("tiktok.com")) {
    return { name: "TikTok", color: "bg-[#010101] hover:bg-[#333] border border-white/20", icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg> };
  } else if (l.includes("instagram.com")) {
    return { name: "Instagram", color: "bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:opacity-90", icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg> };
  }
  return null;
};

// Helper: render description with clickable links (URLs removed if shown as buttons)
const RenderDescription = ({ text, shownUrls }) => {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/gi;
  // Remove URLs that are already shown as buttons
  let cleanText = text;
  (shownUrls || []).forEach(url => { cleanText = cleanText.replace(url, '').trim(); });
  // Remove empty lines left behind
  cleanText = cleanText.replace(/\n\s*\n\s*\n/g, '\n\n').trim();
  if (!cleanText) return null;
  const parts = cleanText.split(urlRegex);
  return (
    <p className="text-white drop-shadow whitespace-pre-wrap">
      {parts.map((part, i) =>
        urlRegex.test(part) ? (
          <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-[#00d4ff] hover:text-[#00bfe6] underline break-all">{part}</a>
        ) : part
      )}
    </p>
  );
};

// ─── Danish Holidays Engine ───
const getEasterDate = (year) => {
  // Anonymous Gregorian algorithm
  const a = year % 19, b = Math.floor(year / 100), c = year % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month, day);
};

const getDanishHolidays = (year) => {
  const easter = getEasterDate(year);
  const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

  const holidays = {};
  const add = (date, name) => { holidays[fmt(date)] = name; };

  // Fixed holidays
  add(new Date(year, 0, 1), "Nytårsdag");
  add(new Date(year, 5, 5), "Grundlovsdag");
  add(new Date(year, 11, 24), "Juleaften");
  add(new Date(year, 11, 25), "Juledag");
  add(new Date(year, 11, 26), "2. Juledag");
  add(new Date(year, 11, 31), "Nytårsaften");

  // Easter-based moveable holidays
  add(addDays(easter, -3), "Skærtorsdag");
  add(addDays(easter, -2), "Langfredag");
  add(easter, "Påskedag");
  add(addDays(easter, 1), "2. Påskedag");
  add(addDays(easter, 39), "Kr. Himmelfartsdag");
  add(addDays(easter, 49), "Pinsedag");
  add(addDays(easter, 50), "2. Pinsedag");

  return holidays;
};

const CalendarPage = () => {
  const { member, isAdmin, isMember, getToken } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedEventDetail, setSelectedEventDetail] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showSignupForm, setShowSignupForm] = useState(false);
  const [showSignups, setShowSignups] = useState(false);
  const [signups, setSignups] = useState([]);
  const [signupCount, setSignupCount] = useState({});
  const [signupForm, setSignupForm] = useState({ name: "", email: "", phone: "", comment: "" });
  const [submittingSignup, setSubmittingSignup] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);
  const [savedEvent, setSavedEvent] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: "",
    end_date: "",
    departure_time: "",
    location: "",
    event_type: "event",
    is_public: true,
    meeting_minutes: "",
    meeting_files: [],
    image: "",
    link_1: "",
    link_2: "",
    send_newsletter: false,
  });

  const isModerator = member?.role === "editor";
  const canEdit = isAdmin || isModerator;
  const canCreate = isMember; // All logged-in members can create
  const canChooseVisibility = isAdmin || isModerator;

  // Lock body scroll when event detail modal is open
  useEffect(() => {
    if (selectedEventDetail) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [selectedEventDetail]);

  const getAuthHeaders = useCallback(() => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getToken]);

  const eventTypes = [
    { value: "event", label: "Event", color: "bg-blue-500", dotColor: "bg-blue-500", icon: "🎉", public: true },
    { value: "køretur", label: "Køretur", color: "bg-green-500", dotColor: "bg-green-500", icon: "🏍️", public: true },
    { value: "møde", label: "Møde", color: "bg-amber-500", dotColor: "bg-amber-500", icon: "📋", public: false },
    { value: "referat", label: "Referat", color: "bg-purple-500", dotColor: "bg-purple-500", icon: "📄", public: false },
  ];

  const getEventTypeConfig = (type) => eventTypes.find(t => t.value === type) || eventTypes[0];

  const fetchSignupCount = useCallback(async (eventId) => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/signup-count`);
      setSignupCount(prev => ({ ...prev, [eventId]: res.data.count }));
    } catch { /* ignore */ }
  }, []);

  const fetchSignups = async (eventId) => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/signups`, { headers: getAuthHeaders() });
      setSignups(res.data);
    } catch { setSignups([]); }
  };

  // Track own signups via localStorage
  const getMySignupIds = () => {
    try { return JSON.parse(localStorage.getItem("my_signups") || "{}"); } catch { return {}; }
  };
  const saveMySignupId = (eventId, signupId) => {
    const mine = getMySignupIds();
    mine[eventId] = signupId;
    localStorage.setItem("my_signups", JSON.stringify(mine));
  };
  const removeMySignupId = (eventId) => {
    const mine = getMySignupIds();
    delete mine[eventId];
    localStorage.setItem("my_signups", JSON.stringify(mine));
  };
  const isMySignup = (eventId, signupId) => {
    const mine = getMySignupIds();
    return mine[eventId] === signupId;
  };
  const amISignedUp = (eventId) => {
    const mine = getMySignupIds();
    return !!mine[eventId];
  };

  const handleSignup = async (eventId) => {
    setSubmittingSignup(true);
    try {
      const body = isMember
        ? { comment: signupForm.comment, phone: signupForm.phone }
        : { name: signupForm.name, email: signupForm.email, phone: signupForm.phone, comment: signupForm.comment };
      const res = await axios.post(`${API}/events/${eventId}/signup`, body, { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } });
      saveMySignupId(eventId, res.data.signup_id);
      toast.success("Du er nu tilmeldt!");
      setShowSignupForm(false);
      setSignupForm({ name: "", email: "", phone: "", comment: "" });
      fetchSignupCount(eventId);
      if (showSignups) fetchSignups(eventId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke tilmelde");
    } finally {
      setSubmittingSignup(false);
    }
  };

  const handleCancelSignup = async (eventId, signupId) => {
    try {
      await axios.delete(`${API}/events/${eventId}/signups/${signupId}`, { headers: getAuthHeaders() });
      removeMySignupId(eventId);
      toast.success("Tilmelding afmeldt");
      fetchSignups(eventId);
      fetchSignupCount(eventId);
    } catch (err) {
      toast.error("Kunne ikke afmelde");
    }
  };

  const handleSelfCancel = async (eventId) => {
    const mine = getMySignupIds();
    const signupId = mine[eventId];
    if (!signupId) return;
    try {
      await axios.delete(`${API}/events/${eventId}/signups/${signupId}`);
      removeMySignupId(eventId);
      toast.success("Din tilmelding er afmeldt");
      fetchSignupCount(eventId);
      if (showSignups) fetchSignups(eventId);
    } catch (err) {
      toast.error("Kunne ikke afmelde");
    }
  };

  const fetchEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/events`, { headers: getAuthHeaders() });
      setEvents(res.data);
    } catch {
      toast.error("Kunne ikke hente events");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);
  useEffect(() => { events.forEach(e => fetchSignupCount(e.event_id)); }, [events, fetchSignupCount]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSavingEvent(true);
    try {
      const isPublic = (isMember && !canChooseVisibility) ? false : formData.is_public;
      const payload = {
        ...formData,
        is_public: isPublic,
        date: new Date(formData.date).toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        departure_time: formData.departure_time || null,
      };

      const headers = getAuthHeaders();
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.event_id}`, payload, { headers });
        toast.success("Event opdateret");
      } else {
        await axios.post(`${API}/events`, payload, { headers });
        toast.success("Event oprettet");
      }
      
      setSavedEvent(true);
      setTimeout(() => { setSavedEvent(false); setShowForm(false); }, 1200);
      setEditingEvent(null);
      setFormData({ title: "", description: "", date: "", end_date: "", departure_time: "", location: "", event_type: "event", is_public: true, meeting_minutes: "", image: "", link_1: "", link_2: "", send_newsletter: false });
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fejl ved gem af event");
    } finally {
      setSavingEvent(false);
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm("Er du sikker på du vil slette dette event?")) return;
    try {
      await axios.delete(`${API}/events/${eventId}`, { headers: getAuthHeaders() });
      toast.success("Event slettet");
      setSelectedEventDetail(null);
      setSelectedDay(null);
      fetchEvents();
    } catch {
      toast.error("Kunne ikke slette event");
    }
  };

  const openEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description || "",
      date: new Date(event.date).toISOString().slice(0, 16),
      end_date: event.end_date ? new Date(event.end_date).toISOString().slice(0, 16) : "",
      location: event.location || "",
      event_type: event.event_type || "event",
      is_public: event.is_public !== undefined ? event.is_public : true,
      meeting_minutes: event.meeting_minutes || "",
      meeting_files: event.meeting_files || [],
      image: event.image || "",
      link_1: event.link_1 || "",
      link_2: event.link_2 || "",
      departure_time: event.departure_time || "",
      send_newsletter: event.send_newsletter || false,
    });
    setSelectedEventDetail(null);
    setShowForm(true);
  };

  const openCreateForDate = (date) => {
    setEditingEvent(null);
    const dateStr = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0).toISOString().slice(0, 16);
    const defaultPublic = canChooseVisibility;
    setFormData({ 
      title: "", description: "", date: dateStr, end_date: "", location: "", 
      event_type: "event", is_public: defaultPublic, meeting_minutes: "", image: "",
      link_1: "", link_2: "", send_newsletter: false,
    });
    setShowForm(true);
  };

  const openCreateMeeting = () => {
    setEditingEvent(null);
    setFormData({ 
      title: "", description: "", date: new Date().toISOString().slice(0, 16), 
      end_date: "", departure_time: "", location: "", event_type: "møde", is_public: false, 
      meeting_minutes: "", image: "",
      link_1: "", link_2: "", send_newsletter: false,
    });
    setShowForm(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    setUploadingImage(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/events/upload-image`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        timeout: 30000
      });
      setFormData(prev => ({ ...prev, image: res.data.url }));
      toast.success("Billede uploadet");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fejl ved upload");
    } finally {
      setUploadingImage(false);
    }
  };

  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => {
    const day = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return day === 0 ? 6 : day - 1;
  };
  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString("da-DK", { day: "numeric", month: "long", year: "numeric" });
  const formatTime = (dateStr) => new Date(dateStr).toLocaleTimeString("da-DK", { hour: "2-digit", minute: "2-digit" });

  // Check if an event spans multiple calendar days
  const isMultiDayEvent = (event) => {
    if (!event.end_date) return false;
    const start = new Date(event.date);
    const end = new Date(event.end_date);
    return start.toDateString() !== end.toDateString();
  };

  const getEventsForDay = (day) => events.filter(event => {
    const eventStart = new Date(event.date);
    if (!event.end_date || !isMultiDayEvent(event)) {
      return eventStart.getDate() === day && eventStart.getMonth() === currentDate.getMonth() && eventStart.getFullYear() === currentDate.getFullYear();
    }
    const eventEnd = new Date(event.end_date);
    const cellDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const cellEnd = new Date(currentDate.getFullYear(), currentDate.getMonth(), day, 23, 59, 59);
    return eventStart <= cellEnd && eventEnd >= cellDate;
  });

  // Get position of a day within a multi-day event span
  const getMultiDayPosition = (event, day) => {
    const s = new Date(event.date);
    const e = new Date(event.end_date);
    const isStart = s.getDate() === day && s.getMonth() === currentDate.getMonth() && s.getFullYear() === currentDate.getFullYear();
    const isEnd = e.getDate() === day && e.getMonth() === currentDate.getMonth() && e.getFullYear() === currentDate.getFullYear();
    return { isStart, isEnd, isMiddle: !isStart && !isEnd };
  };

  // Format date range display for events
  const formatDateRange = (event) => {
    if (!isMultiDayEvent(event)) {
      return `${formatDate(event.date)} kl. ${formatTime(event.date)}${event.end_date ? ` - ${formatTime(event.end_date)}` : ''}`;
    }
    const start = new Date(event.date);
    const end = new Date(event.end_date);
    const startStr = start.toLocaleDateString("da-DK", { day: "numeric", month: "long" });
    const endStr = end.toLocaleDateString("da-DK", { day: "numeric", month: "long", year: "numeric" });
    return `${startStr} - ${endStr}`;
  };

  // Holidays for current displayed year(s)
  const holidays = {
    ...getDanishHolidays(currentDate.getFullYear()),
    ...getDanishHolidays(currentDate.getFullYear() + 1),
    ...getDanishHolidays(currentDate.getFullYear() - 1),
  };

  const getHolidayForDay = (day) => {
    const key = `${currentDate.getFullYear()}-${String(currentDate.getMonth()+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    return holidays[key] || null;
  };

  const isWeekend = (day) => {
    const d = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const dow = d.getDay();
    return dow === 0 || dow === 6;
  };

  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  const goToToday = () => setCurrentDate(new Date());
  const wheelTimeout = useRef(null);
  const eventDetailRef = useRef(null);

  // Open event detail in modal overlay with the requested mode visible
  const openEventDetail = (event, mode = "details") => {
    setSelectedEventDetail(event);
    setShowSignupForm(mode === "signup");
    setShowSignups(mode === "participants");
    if (mode === "participants") {
      fetchSignups(event.event_id);
    }
  };

  const upcomingEvents = events
    .filter(event => {
      const now = new Date();
      // Show if start date is in the future, OR if it's a multi-day event still ongoing
      if (new Date(event.date) >= now) return true;
      if (event.end_date && new Date(event.end_date) >= now) return true;
      return false;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 5);

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDayOfMonth = getFirstDayOfMonth(currentDate);
  const dayNames = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"];
  const today = new Date();

  // ISO week number calculation
  const getISOWeekNumber = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
    const week1 = new Date(d.getFullYear(), 0, 4);
    return 1 + Math.round(((d - week1) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
  };

  // Group days into week rows
  const calendarWeeks = (() => {
    const weeks = [];
    let row = Array.from({ length: firstDayOfMonth }, () => null);
    for (let day = 1; day <= daysInMonth; day++) {
      row.push(day);
      if (row.length === 7) { weeks.push(row); row = []; }
    }
    if (row.length > 0) {
      while (row.length < 7) row.push(null);
      weeks.push(row);
    }
    return weeks;
  })();

  const getWeekNum = (week) => {
    const day = week.find(d => d !== null);
    if (!day) return '';
    return getISOWeekNumber(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
  };

  return (
    <div 
      className="min-h-screen pt-20 pb-12 px-4 relative"
      data-testid="calendar-page"
      style={{
        backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.6)), url('https://customer-assets.emergentagent.com/job_00f587e7-4714-4720-98a9-b623d9c78756/artifacts/ayrjtulz_IMG_3866.jpeg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-['Oswald'] font-bold chrome-text">KALENDER</h1>
            <p className="text-gray-400 mt-1">
              {isMember ? "Se alle klubbens events og interne møder" : "Se offentlige events og køreture"}
            </p>
          </div>
          <div className="flex gap-3">
            {canCreate && (
              <>
                <button
                  onClick={openCreateMeeting}
                  className="ghost-blue-btn px-4 py-2 rounded-lg flex items-center gap-2 text-amber-400 border-amber-500/30 hover:bg-amber-500/10"
                  data-testid="add-meeting-btn"
                >
                  <Lock className="w-4 h-4" /> Tilføj møde
                </button>
                <button
                  onClick={() => {
                    setEditingEvent(null);
                    const defaultPublic = canChooseVisibility;
                    setFormData({ title: "", description: "", date: "", end_date: "", departure_time: "", location: "", event_type: "event", is_public: defaultPublic, meeting_minutes: "", image: "", link_1: "", link_2: "", send_newsletter: false });
                    setShowForm(true);
                  }}
                  className="chrome-btn px-6 py-3 rounded-lg flex items-center gap-2"
                  data-testid="create-event-btn"
                >
                  <span className="text-xl">+</span> Tilføj event
                </button>
              </>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex items-center gap-2 text-sm">
            <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded-full bg-emerald-500 text-white">2</span>
            <span className="text-gray-300">Offentlige events</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded-full bg-amber-500 text-black">1</span>
            <span className="text-gray-300 flex items-center gap-1"><Lock className="w-3 h-3 text-amber-400" /> Interne events</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="w-3 h-3 rounded-full bg-red-500/40 border border-red-500/50"></span>
            <span className="text-gray-300">Helligdag</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="w-6 h-1 rounded-full bg-cyan-400"></span>
            <span className="text-gray-300">Flere dage</span>
          </div>
          {eventTypes.filter(t => isMember || t.public).map(type => (
            <div key={type.value} className="flex items-center gap-2 text-sm">
              <span className={`w-3 h-3 rounded-full ${type.dotColor}`}></span>
              <span className="text-gray-300">{type.icon} {type.label}</span>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : (
          <div className="grid lg:grid-cols-4 gap-6">
            <div className={isMember ? "lg:col-span-3" : "lg:col-span-4"}>
              {/* Calendar grid */}
              <div className="rounded-xl p-4 sm:p-6 backdrop-blur-md bg-black/60 border border-white/10"
                onTouchStart={(e) => { e.currentTarget._touchStartX = e.touches[0].clientX; }}
                onTouchEnd={(e) => {
                  const diff = e.changedTouches[0].clientX - (e.currentTarget._touchStartX || 0);
                  if (Math.abs(diff) > 60) { diff > 0 ? prevMonth() : nextMonth(); }
                }}
                onWheel={(e) => {
                  if (wheelTimeout.current) return;
                  if (Math.abs(e.deltaY) > 20) {
                    e.deltaY > 0 ? nextMonth() : prevMonth();
                    wheelTimeout.current = setTimeout(() => { wheelTimeout.current = null; }, 400);
                  }
                }}
                data-testid="calendar-grid"
              >
                <div className="flex items-center justify-between mb-6">
                  <button onClick={prevMonth} className="ghost-blue-btn px-4 py-2 rounded-lg text-lg" data-testid="prev-month-btn">←</button>
                  <div className="text-center">
                    <h2 className="text-2xl sm:text-3xl font-['Oswald'] font-bold text-white drop-shadow-lg">
                      {currentDate.toLocaleDateString("da-DK", { month: "long", year: "numeric" }).toUpperCase()}
                    </h2>
                    <button onClick={goToToday} className="text-blue-400 text-sm hover:text-blue-300 mt-1">I dag</button>
                  </div>
                  <button onClick={nextMonth} className="ghost-blue-btn px-4 py-2 rounded-lg text-lg" data-testid="next-month-btn">→</button>
                </div>

                <div className="grid grid-cols-[1.5rem_repeat(7,1fr)] sm:grid-cols-[2rem_repeat(7,1fr)] gap-1 mb-2">
                  <div className="flex items-center justify-center text-[9px] sm:text-[10px] font-['Oswald'] py-2 text-gray-600">UGE</div>
                  {dayNames.map((day, idx) => (
                    <div key={day} className={`text-center text-sm font-['Oswald'] py-2 font-semibold drop-shadow ${idx >= 5 ? 'text-gray-500' : 'text-gray-300'}`}>{day}</div>
                  ))}
                </div>

                <div className="grid grid-cols-[1.5rem_repeat(7,1fr)] sm:grid-cols-[2rem_repeat(7,1fr)] gap-1">
                  {calendarWeeks.map((week, weekIdx) => {
                    const weekNum = getWeekNum(week);
                    return (
                      <React.Fragment key={`week-${weekIdx}`}>
                        <div className="flex items-center justify-center text-[9px] sm:text-[10px] text-gray-500 font-medium font-['Oswald']" data-testid={`week-number-${weekNum}`}>
                          {weekNum}
                        </div>
                        {week.map((day, cellIdx) => {
                          if (day === null) return <div key={`empty-${weekIdx}-${cellIdx}`} className="aspect-square p-1" />;
                          
                          const dayEvents = getEventsForDay(day);
                          const multiDayEvents = dayEvents.filter(e => isMultiDayEvent(e));
                          const singleDayEvents = dayEvents.filter(e => !isMultiDayEvent(e));
                          const holiday = getHolidayForDay(day);
                          const weekend = isWeekend(day);
                          const isToday = today.getDate() === day && today.getMonth() === currentDate.getMonth() && today.getFullYear() === currentDate.getFullYear();
                          const isSelected = selectedDay === day;
                          const publicEvents = singleDayEvents.filter(e => e.is_public);
                          const internalEvents = singleDayEvents.filter(e => !e.is_public);
                          const hasContent = dayEvents.length > 0 || holiday;
                          const hasMultiDay = multiDayEvents.length > 0;
                          
                          return (
                            <div
                              key={day}
                              onClick={() => { setSelectedDay(day); setSelectedEventDetail(null); }}
                              className={`aspect-square p-1 sm:p-1.5 rounded-lg cursor-pointer transition-all relative overflow-hidden
                                ${isToday ? 'ring-2 ring-blue-500 bg-blue-600/20' : ''}
                                ${isSelected ? 'bg-blue-600/40' : 'hover:bg-white/15'}
                                ${weekend && !isSelected && !isToday ? 'bg-white/[0.04]' : ''}
                                ${hasMultiDay && !isSelected && !isToday ? 'bg-cyan-500/10' : ''}
                              `}
                              data-testid={`calendar-day-${day}`}
                            >
                              <div className="flex flex-col h-full relative z-10">
                                <div className="flex items-center justify-between">
                                  <span className={`text-xs sm:text-sm font-semibold drop-shadow-md ${
                                    isToday ? 'text-blue-300' : weekend ? 'text-gray-500' : 'text-white'
                                  }`}>{day}</span>
                                  {dayEvents.some(e => !e.is_public) && <Lock className="w-2.5 h-2.5 text-amber-400" />}
                                </div>
                                {holiday && (
                                  <span className="text-[8px] sm:text-[10px] text-red-400 leading-tight truncate mt-0.5 font-medium" title={holiday}>
                                    {holiday}
                                  </span>
                                )}
                                {hasContent && (
                                  <div className="flex-1 flex items-end justify-center gap-0.5 mt-0.5 flex-wrap">
                                    {publicEvents.length > 0 && (
                                      <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] sm:min-w-[22px] sm:h-[22px] px-0.5 text-[10px] sm:text-xs font-bold rounded-full bg-emerald-500 text-white shadow-lg" style={{ boxShadow: '0 0 8px rgba(16,185,129,0.4)' }}>
                                        {publicEvents.length}
                                      </span>
                                    )}
                                    {internalEvents.length > 0 && (
                                      <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] sm:min-w-[22px] sm:h-[22px] px-0.5 text-[10px] sm:text-xs font-bold rounded-full bg-amber-500 text-black shadow-lg" style={{ boxShadow: '0 0 8px rgba(245,158,11,0.4)' }}>
                                        {internalEvents.length}
                                      </span>
                                    )}
                                    {holiday && dayEvents.length === 0 && (
                                      <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] sm:min-w-[22px] sm:h-[22px] px-0.5 text-[10px] sm:text-xs font-bold rounded-full bg-red-500/20 text-red-400 border border-red-500/30">
                                        1
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              {multiDayEvents.slice(0, 2).map((event, idx) => {
                                const pos = getMultiDayPosition(event, day);
                                return (
                                  <div key={event.event_id}
                                    className={`absolute left-0 right-0 h-[3px] sm:h-1 ${
                                      event.is_public ? 'bg-cyan-400' : 'bg-amber-400'
                                    } ${pos.isStart ? 'rounded-l-full ml-0.5' : ''} ${pos.isEnd ? 'rounded-r-full mr-0.5' : ''}`}
                                    style={{ bottom: `${idx * 5 + 1}px` }}
                                    title={event.title}
                                  />
                                );
                              })}
                            </div>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              {/* Selected day detail */}
              {selectedDay && (
                <div className="rounded-xl p-4 sm:p-6 mt-6 backdrop-blur-md bg-black/50 border border-white/10">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-['Oswald'] font-bold text-white drop-shadow-lg">
                      {selectedDay}. {currentDate.toLocaleDateString("da-DK", { month: "long" })}
                      {isWeekend(selectedDay) && <span className="ml-2 text-sm text-gray-500 font-normal">(Weekend)</span>}
                    </h3>
                    <div className="flex gap-2">
                      {canCreate && (
                        <button onClick={() => openCreateForDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), selectedDay))} className="ghost-blue-btn px-4 py-2 rounded-lg text-sm" data-testid="add-event-for-day-btn">+ Tilføj event</button>
                      )}
                    </div>
                  </div>

                  {/* Holiday banner */}
                  {getHolidayForDay(selectedDay) && (
                    <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-3" data-testid="holiday-banner">
                      <span className="text-red-400 text-lg">*</span>
                      <div>
                        <p className="text-red-300 font-['Oswald'] font-medium tracking-wide">{getHolidayForDay(selectedDay)}</p>
                        <p className="text-red-400/60 text-xs">Helligdag</p>
                      </div>
                    </div>
                  )}
                  
                  {getEventsForDay(selectedDay).length === 0 && !getHolidayForDay(selectedDay) ? (
                    <p className="text-gray-400 py-4 drop-shadow">Ingen events denne dag</p>
                  ) : (
                    <div className="space-y-3">
                      {getEventsForDay(selectedDay).map(event => {
                        const typeConfig = getEventTypeConfig(event.event_type);
                        const isInternal = !event.is_public;
                        const count = signupCount[event.event_id] || 0;
                        return (
                          <div key={event.event_id} 
                            className={`p-4 rounded-lg border-l-4 transition-all backdrop-blur-sm ${
                              isInternal 
                                ? 'border-l-amber-500 bg-amber-500/5' 
                                : `${typeConfig.color.replace('bg-', 'border-')} bg-black/20`
                            } ${selectedEventDetail?.event_id === event.event_id ? 'bg-white/20' : ''}`} 
                            data-testid={`event-card-${event.event_id}`}>
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span>{typeConfig.icon}</span>
                                  <h4 className="text-lg font-bold text-white">{event.title}</h4>
                                  {isInternal && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-amber-500/20 text-amber-400 border border-amber-500/30">
                                      <Lock className="w-3 h-3" /> Intern
                                    </span>
                                  )}
                                  {isMultiDayEvent(event) && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-cyan-500/20 text-cyan-400 border border-cyan-500/30" data-testid={`multi-day-badge-${event.event_id}`}>
                                      Flere dage
                                    </span>
                                  )}
                                </div>
                                <p className="text-blue-400 text-sm mt-1">{isMultiDayEvent(event) ? formatDateRange(event) : `kl. ${formatTime(event.date)}${event.end_date ? ` - ${formatTime(event.end_date)}` : ''}`}</p>
                                {event.departure_time && <p className="text-amber-300 text-sm mt-0.5">Afgang fra klubben kl. {event.departure_time}</p>}
                                {event.location && <p className="text-gray-400 text-sm mt-1">📍 {event.location}</p>}
                              </div>
                              <span className={`px-2 py-1 rounded text-xs ${typeConfig.color} text-white`}>{typeConfig.label}</span>
                            </div>
                            {/* Signup count + actions */}
                            <div className="flex items-center gap-3 mt-3 flex-wrap">
                              <div className="flex items-center gap-1.5 text-sm" data-testid={`event-card-signup-count-${event.event_id}`}>
                                <Users className="w-4 h-4 text-emerald-400" />
                                <span className="text-emerald-400 font-bold">{count}</span>
                                <span className="text-gray-400">tilmeldt</span>
                              </div>
                              {amISignedUp(event.event_id) ? (
                                <button onClick={(e) => { e.stopPropagation(); handleSelfCancel(event.event_id); }}
                                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-all"
                                  data-testid={`event-card-cancel-btn-${event.event_id}`}>
                                  Afmeld dig
                                </button>
                              ) : (
                                <button onClick={(e) => { e.stopPropagation(); openEventDetail(event, "signup"); }} 
                                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30 transition-all"
                                  data-testid={`event-card-signup-btn-${event.event_id}`}>
                                  Tilmeld dig
                                </button>
                              )}
                              <button onClick={(e) => { e.stopPropagation(); openEventDetail(event, "participants"); }} 
                                className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-400 hover:text-white border border-gray-700 hover:border-gray-500 transition-all"
                                data-testid={`event-card-view-signups-${event.event_id}`}>
                                Se deltagere
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); openEventDetail(event, "details"); }} className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-blue-400 hover:text-blue-300 transition-all" data-testid={`read-more-btn-${event.event_id}`}>
                                Læs mere →
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Event detail (modal overlay) */}
              {selectedEventDetail && (
                <div
                  className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm overflow-y-auto"
                  onClick={(e) => { if (e.target === e.currentTarget) { setSelectedEventDetail(null); setShowSignupForm(false); setShowSignups(false); } }}
                  data-testid="event-detail-modal"
                >
                  <div className="min-h-full flex items-start justify-center p-3 sm:p-6">
                    <div ref={eventDetailRef} className="rounded-xl p-4 sm:p-6 my-6 backdrop-blur-md bg-neutral-950/95 border border-white/10 w-full max-w-2xl shadow-2xl">
                  {selectedEventDetail.image && (
                    <div className="mb-4 -mx-4 sm:-mx-6 -mt-4 sm:-mt-6">
                      <img src={selectedEventDetail.image.startsWith('http') ? selectedEventDetail.image : `${BACKEND_URL}${selectedEventDetail.image}`} alt={selectedEventDetail.title} className="w-full h-48 sm:h-64 object-cover rounded-t-xl" />
                    </div>
                  )}
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-2xl">{getEventTypeConfig(selectedEventDetail.event_type).icon}</span>
                        <h3 className="text-2xl font-['Oswald'] font-bold text-white drop-shadow-lg">{selectedEventDetail.title}</h3>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-3 py-1 rounded text-sm ${getEventTypeConfig(selectedEventDetail.event_type).color} text-white`}>{getEventTypeConfig(selectedEventDetail.event_type).label}</span>
                        {selectedEventDetail.is_public ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-green-600/30 text-green-300 border border-green-500/30">
                            <Globe className="w-3 h-3" /> Offentlig
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-amber-600/30 text-amber-300 border border-amber-500/30">
                            <Lock className="w-3 h-3" /> Intern — kun for medlemmer
                          </span>
                        )}
                        {isMultiDayEvent(selectedEventDetail) && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-cyan-500/20 text-cyan-300 border border-cyan-500/30" data-testid="detail-multi-day-badge">
                            Flere dage
                          </span>
                        )}
                      </div>
                    </div>
                    <button onClick={() => { setSelectedEventDetail(null); setShowSignupForm(false); setShowSignups(false); }} className="text-gray-400 hover:text-white text-3xl leading-none" data-testid="event-detail-close">×</button>
                  </div>

                  {/* Quick-action panel (shows immediately at top when triggered from card) */}
                  {showSignupForm && !amISignedUp(selectedEventDetail.event_id) && (
                    <div className="mb-6 bg-emerald-500/10 backdrop-blur-sm rounded-lg p-4 border border-emerald-500/40 space-y-3" data-testid="quick-signup-form">
                      <h4 className="text-emerald-300 font-bold text-base flex items-center gap-2">
                        <Users className="w-4 h-4" /> Tilmeld dig
                      </h4>
                      {!isMember && (
                        <input type="text" placeholder="Dit navn *" value={signupForm.name} onChange={e => setSignupForm(p => ({ ...p, name: e.target.value }))}
                          className="w-full px-3 py-2 bg-black/40 border border-gray-700 text-white text-sm rounded focus:border-emerald-500/50 focus:outline-none" data-testid="quick-signup-name" />
                      )}
                      {!isMember && (
                        <input type="email" placeholder="Email (valgfrit)" value={signupForm.email} onChange={e => setSignupForm(p => ({ ...p, email: e.target.value }))}
                          className="w-full px-3 py-2 bg-black/40 border border-gray-700 text-white text-sm rounded focus:border-emerald-500/50 focus:outline-none" data-testid="quick-signup-email" />
                      )}
                      <div>
                        <input type="tel" placeholder="Telefonnummer (valgfrit)" value={signupForm.phone} onChange={e => setSignupForm(p => ({ ...p, phone: e.target.value }))}
                          className="w-full px-3 py-2 bg-black/40 border border-gray-700 text-white text-sm rounded focus:border-emerald-500/50 focus:outline-none" data-testid="quick-signup-phone" />
                        <p className="text-gray-500 text-xs mt-1 flex items-center gap-1">
                          <Phone className="w-3 h-3" /> Kun synligt for Road Captain
                        </p>
                      </div>
                      <textarea placeholder="Kommentar (valgfrit)" value={signupForm.comment} onChange={e => setSignupForm(p => ({ ...p, comment: e.target.value }))}
                        rows={2} className="w-full px-3 py-2 bg-black/40 border border-gray-700 text-white text-sm rounded focus:border-emerald-500/50 focus:outline-none resize-none" data-testid="quick-signup-comment" />
                      <button
                        onClick={() => handleSignup(selectedEventDetail.event_id)}
                        disabled={submittingSignup || (!isMember && !signupForm.name.trim())}
                        className="w-full py-2.5 rounded-lg text-sm font-bold bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        data-testid="quick-signup-submit-btn"
                      >
                        {submittingSignup ? "Tilmelder..." : "Bekræft tilmelding"}
                      </button>
                    </div>
                  )}
                  {showSignupForm && amISignedUp(selectedEventDetail.event_id) && (
                    <div className="mb-6 bg-emerald-500/10 backdrop-blur-sm rounded-lg p-4 border border-emerald-500/40 flex items-center justify-between gap-3" data-testid="quick-already-signed-up">
                      <span className="text-emerald-300 text-sm">✓ Du er allerede tilmeldt denne event</span>
                      <button onClick={() => handleSelfCancel(selectedEventDetail.event_id)} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-all">Afmeld dig</button>
                    </div>
                  )}

                  {showSignups && (
                    <div className="mb-6 bg-black/40 backdrop-blur-sm rounded-lg border border-white/20" data-testid="quick-signups-list">
                      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
                        <Users className="w-4 h-4 text-emerald-400" />
                        <h4 className="text-white font-bold text-base">Deltagere ({signupCount[selectedEventDetail.event_id] || 0})</h4>
                      </div>
                      <div className="divide-y divide-white/5 max-h-[40vh] overflow-y-auto">
                        {signups.length === 0 ? (
                          <p className="text-gray-500 text-sm p-4 text-center">Ingen tilmeldinger endnu</p>
                        ) : signups.map(s => (
                          <div key={s.signup_id} className={`p-3 flex items-start justify-between gap-3 ${isMySignup(selectedEventDetail.event_id, s.signup_id) ? 'bg-emerald-500/5 border-l-2 border-l-emerald-500' : ''}`}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-white text-sm font-medium">{s.name}</span>
                                {isMySignup(selectedEventDetail.event_id, s.signup_id) && (
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-600/20 text-emerald-400">Dig</span>
                                )}
                                <span className={`text-xs px-1.5 py-0.5 rounded ${s.signup_type === "intern" ? "bg-amber-600/20 text-amber-400" : "bg-emerald-600/20 text-emerald-400"}`}>
                                  {s.signup_type === "intern" ? "Medlem" : "Deltager"}
                                </span>
                              </div>
                              {canEdit && s.phone && <p className="text-gray-400 text-xs mt-0.5 flex items-center gap-1"><Phone className="w-3 h-3" /> {s.phone}</p>}
                              {canEdit && s.email && <p className="text-gray-400 text-xs">{s.email}</p>}
                              {canEdit && s.comment && <p className="text-gray-300 text-xs mt-1 flex items-start gap-1"><MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" /> {s.comment}</p>}
                            </div>
                            {isMySignup(selectedEventDetail.event_id, s.signup_id) && (
                              <button onClick={() => handleSelfCancel(selectedEventDetail.event_id)}
                                className="text-red-500 hover:text-red-400 p-1 flex-shrink-0 text-xs flex items-center gap-1" title="Afmeld dig" data-testid={`quick-self-cancel-${s.signup_id}`}>
                                <Trash2 className="w-3.5 h-3.5" /> Afmeld
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    <div className="flex items-center gap-3 text-white">
                      <CalendarIcon className="w-5 h-5 text-blue-400" />
                      <span className="drop-shadow">{formatDateRange(selectedEventDetail)}</span>
                    </div>
                    {selectedEventDetail.location && (
                      <div className="flex items-center gap-3 text-white"><span className="text-blue-400">📍</span><span className="drop-shadow">{selectedEventDetail.location}</span></div>
                    )}
                    {selectedEventDetail.departure_time && (
                      <div className="flex items-center gap-3 text-white" data-testid="detail-departure-time"><span className="text-amber-400">🏍</span><span className="drop-shadow">Afgang fra klubben kl. {selectedEventDetail.departure_time}</span></div>
                    )}
                    {selectedEventDetail.description && (() => {
                      const descUrls = extractUrls(selectedEventDetail.description);
                      const allLinkUrls = [selectedEventDetail.link_1, selectedEventDetail.link_2].filter(Boolean);
                      const descSocialUrls = descUrls.filter(u => getPlatform(u) && !allLinkUrls.includes(u));
                      const allShownUrls = [...allLinkUrls, ...descSocialUrls];
                      return (
                        <>
                          <div className="bg-black/30 backdrop-blur-sm rounded-lg p-4 border border-white/10">
                            <RenderDescription text={selectedEventDetail.description} shownUrls={allShownUrls} />
                          </div>
                          {descSocialUrls.length > 0 && !selectedEventDetail.link_1 && !selectedEventDetail.link_2 && (
                            <div className="flex flex-wrap gap-3">
                              {descSocialUrls.map((url, i) => {
                                const platform = getPlatform(url);
                                return (
                                  <a key={`desc-link-${i}`} href={url} target="_blank" rel="noopener noreferrer"
                                    className={`flex items-center gap-2.5 px-5 py-3 rounded-xl text-white font-medium text-sm transition-all shadow-lg ${platform.color}`}
                                    data-testid={`event-desc-link-${i + 1}`}
                                  >
                                    {platform.icon || <ExternalLink className="w-5 h-5" />}
                                    <span>{platform.name}</span>
                                  </a>
                                );
                              })}
                            </div>
                          )}
                        </>
                      );
                    })()}
                    {(selectedEventDetail.link_1 || selectedEventDetail.link_2) && (
                      <div className="flex flex-wrap gap-3">
                        {[selectedEventDetail.link_1, selectedEventDetail.link_2].filter(Boolean).map((link, i) => {
                          const platform = getPlatform(link) || { name: "Link", color: "bg-gray-700 hover:bg-gray-600", icon: null };
                          return (
                            <a key={i} href={link} target="_blank" rel="noopener noreferrer"
                              className={`flex items-center gap-2.5 px-5 py-3 rounded-xl text-white font-medium text-sm transition-all shadow-lg ${platform.color}`}
                              data-testid={`event-detail-link-${i + 1}`}
                            >
                              {platform.icon || <ExternalLink className="w-5 h-5" />}
                              <span>{platform.name}</span>
                            </a>
                          );
                        })}
                      </div>
                    )}
                    {isMember && selectedEventDetail.event_type === "referat" && (selectedEventDetail.meeting_minutes || (selectedEventDetail.meeting_files && selectedEventDetail.meeting_files.length > 0)) && (
                      <div className="bg-purple-900/30 backdrop-blur-sm border border-purple-500/30 rounded-lg p-4">
                        <h4 className="text-purple-300 font-bold mb-2 drop-shadow">Mødereferat</h4>
                        {selectedEventDetail.meeting_minutes && (
                          <p className="text-white drop-shadow whitespace-pre-wrap mb-3">{selectedEventDetail.meeting_minutes}</p>
                        )}
                        {selectedEventDetail.meeting_files && selectedEventDetail.meeting_files.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-purple-300/70 text-xs uppercase tracking-wider">Filer</p>
                            {selectedEventDetail.meeting_files.map((file, idx) => (
                              <a key={idx} href={file.url} target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-2 bg-black/30 border border-purple-500/20 rounded-lg px-3 py-2.5 text-white hover:bg-purple-500/10 transition-all"
                                data-testid={`referat-file-${idx}`}>
                                <FileText className="w-4 h-4 text-purple-400 shrink-0" />
                                <span className="text-sm truncate">{file.name || "Fil"}</span>
                                <ExternalLink className="w-3.5 h-3.5 text-gray-500 shrink-0 ml-auto" />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {/* ─── Tilmelding ─── */}
                    <div className="pt-4 border-t border-white/10 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-white">
                          <Users className="w-4 h-4 text-emerald-400" />
                          <span className="text-emerald-400 font-bold text-lg">{signupCount[selectedEventDetail.event_id] || 0}</span>
                          <span className="text-sm font-medium">tilmeldt</span>
                        </div>
                        <div className="flex gap-2">
                          {amISignedUp(selectedEventDetail.event_id) && (
                            <button
                              onClick={() => handleSelfCancel(selectedEventDetail.event_id)}
                              className="px-4 py-2 rounded-lg text-sm font-medium bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-all"
                              data-testid="event-cancel-btn"
                            >
                              Afmeld dig
                            </button>
                          )}
                          {!amISignedUp(selectedEventDetail.event_id) && (
                            <button
                              onClick={() => { setShowSignupForm(!showSignupForm); setShowSignups(false); }}
                              className="px-4 py-2 rounded-lg text-sm font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30 transition-all"
                              data-testid="event-signup-btn"
                            >
                              Tilmeld dig
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {canEdit && (
                      <div className="flex gap-3 pt-4 border-t border-white/20">
                        <button onClick={() => openEdit(selectedEventDetail)} className="ghost-blue-btn px-4 py-2 rounded-lg" data-testid={`edit-event-${selectedEventDetail.event_id}`}>Rediger</button>
                        <button onClick={() => handleDelete(selectedEventDetail.event_id)} className="text-red-400 hover:text-red-300 px-4 py-2" data-testid={`delete-event-${selectedEventDetail.event_id}`}>Slet</button>
                      </div>
                    )}
                  </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar for members */}
            {isMember && (
              <div className="lg:col-span-1 space-y-4">
                <div className="rounded-xl p-4 sm:p-6 backdrop-blur-md bg-black/30 border border-amber-500/30">
                  <h3 className="text-lg font-['Oswald'] font-bold text-amber-300 mb-2 drop-shadow flex items-center gap-2">
                    <Lock className="w-4 h-4" /> INTERN KALENDER
                  </h3>
                  <p className="text-gray-200 text-sm mb-4 drop-shadow">Opret møder og interne events kun synlige for medlemmer</p>
                  <div className="space-y-2">
                    <button onClick={openCreateMeeting}
                      className="w-full px-4 py-3 rounded-lg text-sm bg-amber-500/20 text-amber-300 border border-amber-500/30 hover:bg-amber-500/30 transition-all flex items-center gap-2 font-medium"
                      data-testid="sidebar-add-meeting-btn">
                      📋 Tilføj møde
                    </button>
                    <button onClick={() => { setEditingEvent(null); setFormData({ title: "", description: "", date: new Date().toISOString().slice(0, 16), end_date: "", location: "", event_type: "referat", is_public: false, meeting_minutes: "", image: "", link_1: "", link_2: "", send_newsletter: false }); setShowForm(true); }}
                      className="w-full px-4 py-3 rounded-lg text-sm bg-purple-500/20 text-purple-300 border border-purple-500/30 hover:bg-purple-500/30 transition-all flex items-center gap-2 font-medium"
                      data-testid="sidebar-add-referat-btn">
                      📄 Tilføj referat
                    </button>
                  </div>
                </div>

                {/* Internal events list */}
                {events.filter(e => !e.is_public).length > 0 && (
                  <div className="rounded-xl p-4 sm:p-6 backdrop-blur-md bg-black/30 border border-amber-500/20">
                    <h3 className="text-sm font-['Oswald'] font-bold text-amber-300 mb-3 flex items-center gap-2">
                      <Lock className="w-3 h-3" /> INTERNE EVENTS
                    </h3>
                    <div className="space-y-2">
                      {events.filter(e => !e.is_public).sort((a, b) => new Date(a.date) - new Date(b.date)).slice(0, 5).map(event => {
                        const typeConfig = getEventTypeConfig(event.event_type);
                        return (
                          <button key={event.event_id}
                            onClick={() => {
                              const d = new Date(event.date);
                              setCurrentDate(new Date(d.getFullYear(), d.getMonth(), 1));
                              setSelectedDay(d.getDate());
                              openEventDetail(event, "details");
                            }}
                            className="w-full text-left p-3 rounded-lg bg-amber-500/5 border border-amber-500/10 hover:border-amber-500/30 transition-all"
                            data-testid={`internal-event-${event.event_id}`}>
                            <div className="flex items-center gap-2 text-sm">
                              <span>{typeConfig.icon}</span>
                              <span className="text-white font-medium truncate">{event.title}</span>
                            </div>
                            <p className="text-amber-400/70 text-xs mt-1">{formatDate(event.date)}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Upcoming events */}
        {upcomingEvents.length > 0 && (
          <div className="mt-8 rounded-xl p-4 sm:p-6 backdrop-blur-md bg-black/30 border border-white/10">
            <h3 className="text-xl font-['Oswald'] font-bold text-white mb-4 drop-shadow-lg">KOMMENDE EVENTS</h3>
            <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory hide-scrollbar" style={{ WebkitOverflowScrolling: 'touch' }}>
              {upcomingEvents.map(event => {
                const typeConfig = getEventTypeConfig(event.event_type);
                const isInternal = !event.is_public;
                return (
                  <div key={event.event_id} 
                    className={`flex-shrink-0 w-72 sm:w-80 snap-start rounded-xl p-4 cursor-pointer transition-all hover:scale-[1.02] backdrop-blur-sm border ${
                      isInternal 
                        ? 'bg-gradient-to-br from-amber-900/20 to-black/20 border-amber-500/30 hover:border-amber-500/50' 
                        : 'bg-gradient-to-br from-black/40 to-black/20 border-white/10 hover:border-blue-500/50'
                    }`}
                    onClick={() => { const d = new Date(event.date); setCurrentDate(new Date(d.getFullYear(), d.getMonth(), 1)); setSelectedDay(d.getDate()); openEventDetail(event, "details"); }} 
                    data-testid={`upcoming-event-${event.event_id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${isInternal ? 'bg-amber-500 text-black' : `${typeConfig.color} text-white`}`}>
                        {typeConfig.icon} {typeConfig.label}
                      </span>
                      {isInternal && (
                        <span className="inline-flex items-center gap-1 text-amber-400 text-xs">
                          <Lock className="w-3 h-3" /> Intern
                        </span>
                      )}
                      {isMultiDayEvent(event) && new Date(event.date) < new Date() && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-500/20 text-cyan-300 border border-cyan-500/30" data-testid={`ongoing-badge-${event.event_id}`}>
                          Igangværende
                        </span>
                      )}
                    </div>
                    <h4 className="text-lg font-bold text-white mb-2 line-clamp-2">{event.title}</h4>
                    <div className="flex items-center gap-2 text-blue-400 text-sm mb-2"><CalendarIcon className="w-4 h-4" /><span>{isMultiDayEvent(event) ? formatDateRange(event) : formatDate(event.date)}</span></div>
                    {event.location && <p className="text-gray-400 text-sm truncate mb-3">📍 {event.location}</p>}
                    {event.departure_time && <p className="text-amber-300 text-sm truncate mb-3">🏍 Afgang kl. {event.departure_time}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Create/Edit form modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto backdrop-blur-md bg-black/80 border border-white/20">
              <h2 className="text-2xl font-['Oswald'] font-bold text-white mb-6 drop-shadow-lg">{editingEvent ? "REDIGER EVENT" : "NYT EVENT"}</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-gray-200 mb-2 drop-shadow">Type *</label>
                  <div className="grid grid-cols-2 gap-2">
                    {eventTypes.map(type => (
                      <button key={type.value} type="button" onClick={() => {
                        const newPublic = type.public ? formData.is_public : false;
                        setFormData({ ...formData, event_type: type.value, is_public: newPublic });
                      }}
                        className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 ${formData.event_type === type.value ? `${type.color} border-transparent text-white` : 'border-neutral-700 text-gray-400 hover:border-neutral-500'}`} data-testid={`event-type-${type.value}`}>
                        <span>{type.icon}</span><span className="text-sm font-medium">{type.label}</span>
                        {!type.public && <Lock className="w-3 h-3 text-amber-300" />}
                      </button>
                    ))}
                  </div>
                  {!eventTypes.find(t => t.value === formData.event_type)?.public && (
                    <p className="text-amber-400 text-xs mt-2 flex items-center gap-1"><Lock className="w-3 h-3" /> Kun synlig for medlemmer</p>
                  )}
                </div>

                {canChooseVisibility && eventTypes.find(t => t.value === formData.event_type)?.public && (
                  <div>
                    <label className="block text-gray-200 mb-2 drop-shadow">Synlighed *</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button type="button" onClick={() => setFormData({ ...formData, is_public: true })} className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 ${formData.is_public ? 'bg-green-600 border-transparent text-white' : 'border-neutral-700 text-gray-400 hover:border-neutral-500'}`} data-testid="visibility-public"><Globe className="w-4 h-4" /><span className="text-sm font-medium">Offentlig</span></button>
                      <button type="button" onClick={() => setFormData({ ...formData, is_public: false })} className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 ${!formData.is_public ? 'bg-amber-600 border-transparent text-white' : 'border-neutral-700 text-gray-400 hover:border-neutral-500'}`} data-testid="visibility-internal"><Lock className="w-4 h-4" /><span className="text-sm font-medium">Intern</span></button>
                    </div>
                    <p className="text-gray-400 text-xs mt-2">{formData.is_public ? "Synlig for alle besøgende" : "Kun synlig for medlemmer (Gruppe 1, 2, 3)"}</p>
                  </div>
                )}

                {isMember && !canChooseVisibility && (
                  <div className="bg-amber-600/20 border border-amber-500/30 rounded-lg p-3">
                    <p className="text-amber-300 text-sm flex items-center gap-2"><Lock className="w-4 h-4" /><span>Dit event vil kun være synligt for medlemmer (internt)</span></p>
                  </div>
                )}

                <div>
                  <label className="block text-gray-300 mb-2">Titel *</label>
                  <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="mc-input w-full" required placeholder="Giv eventet et navn" data-testid="event-title-input" />
                </div>
                
                <div>
                  <label className="block text-gray-300 mb-2">Beskrivelse</label>
                  <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="mc-input w-full h-24 resize-none" placeholder="Beskriv eventet..." data-testid="event-description-input" />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <DateTimePicker value={formData.date} onChange={(v) => setFormData({ ...formData, date: v })} label="Start" required placeholder="Vælg startdato" testId="event-date-input" />
                  <DateTimePicker value={formData.end_date} onChange={(v) => setFormData({ ...formData, end_date: v })} label="Slut" placeholder="Vælg slutdato" testId="event-end-date-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Afgangstid fra klubben</label>
                  <input type="time" value={formData.departure_time} onChange={(e) => setFormData({ ...formData, departure_time: e.target.value })} className="mc-input w-full" data-testid="event-departure-time-input" />
                  <p className="text-gray-500 text-xs mt-1">Tidspunkt for afgang fra klubhuset (valgfrit)</p>
                </div>
                
                <div>
                  <label className="block text-gray-300 mb-2">Sted</label>
                  <input type="text" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="mc-input w-full" placeholder="Hvor foregår eventet?" data-testid="event-location-input" />
                </div>

                <div>
                  <label className="block text-gray-300 mb-2">Billede</label>
                  <div className="space-y-3">
                    {formData.image && (
                      <div className="relative">
                        <img src={formData.image.startsWith('http') ? formData.image : `${BACKEND_URL}${formData.image}`} alt="Event" className="w-full h-40 object-cover rounded-lg" />
                        <button type="button" onClick={() => setFormData({ ...formData, image: "" })} className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-1 rounded-full"><X className="w-4 h-4" /></button>
                      </div>
                    )}
                    <label className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed border-neutral-600 rounded-lg cursor-pointer hover:border-blue-500 transition-colors ${uploadingImage ? 'opacity-50' : ''}`}>
                      <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploadingImage} data-testid="event-image-input" />
                      {uploadingImage ? <><span className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /><span className="text-gray-400">Uploader billede...</span></> : <><Image className="w-5 h-5 text-gray-400" /><span className="text-gray-400">{formData.image ? 'Skift billede' : 'Upload billede'}</span></>}
                    </label>
                  </div>
                </div>

                {/* Link fields */}
                <div className="space-y-3">
                  <label className="block text-gray-300 mb-1">Links (valgfrit)</label>
                  <div className="relative">
                    <ExternalLink className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input type="url" value={formData.link_1} onChange={(e) => setFormData({ ...formData, link_1: e.target.value })} className="mc-input w-full pl-10" placeholder="Link 1 — f.eks. Facebook, TikTok..." data-testid="event-link-1-input" />
                  </div>
                  <div className="relative">
                    <ExternalLink className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input type="url" value={formData.link_2} onChange={(e) => setFormData({ ...formData, link_2: e.target.value })} className="mc-input w-full pl-10" placeholder="Link 2 — f.eks. Instagram, YouTube..." data-testid="event-link-2-input" />
                  </div>
                </div>

                {formData.event_type === "referat" && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-gray-300 mb-2">Mødereferat (tekst - valgfri)</label>
                      <textarea value={formData.meeting_minutes} onChange={(e) => setFormData({ ...formData, meeting_minutes: e.target.value })} className="mc-input w-full h-24 resize-none" placeholder="Kort beskrivelse eller lad feltet stå tomt..." data-testid="event-meeting-minutes-input" />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2">Upload filer (PDF, billeder, dokumenter)</label>
                      <div className="space-y-2">
                        {(formData.meeting_files || []).map((file, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-purple-900/20 border border-purple-500/20 rounded-lg px-3 py-2">
                            <span className="text-purple-300 text-sm truncate flex-1">{file.name || file.url?.split('/').pop()}</span>
                            <button onClick={() => {
                              const files = [...formData.meeting_files];
                              files.splice(idx, 1);
                              setFormData({ ...formData, meeting_files: files });
                            }} className="text-red-400 hover:text-red-300 shrink-0">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        <label className="flex items-center gap-2 px-4 py-3 rounded-lg bg-purple-500/10 border border-purple-500/30 text-purple-300 hover:bg-purple-500/20 cursor-pointer transition-all text-sm" data-testid="upload-referat-file-btn">
                          <Upload className="w-4 h-4" /> Upload fil
                          <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.txt" className="hidden" onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const fd = new FormData();
                              fd.append("file", file);
                              const token = getToken();
                              const res = await axios.post(`${API}/events/upload-image`, fd, {
                                headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
                              });
                              const url = res.data.url || res.data.file_url || res.data.path;
                              setFormData(prev => ({
                                ...prev,
                                meeting_files: [...(prev.meeting_files || []), { name: file.name, url }],
                              }));
                              toast.success(`${file.name} uploadet!`);
                            } catch { toast.error("Kunne ikke uploade fil"); }
                            e.target.value = "";
                          }} />
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Newsletter toggle */}
                {formData.event_type !== "referat" && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20" data-testid="newsletter-toggle-area">
                    <input
                      type="checkbox"
                      id="send_newsletter"
                      checked={formData.send_newsletter}
                      onChange={(e) => setFormData({ ...formData, send_newsletter: e.target.checked })}
                      className="w-4 h-4 accent-amber-500 cursor-pointer"
                      data-testid="newsletter-checkbox"
                    />
                    <label htmlFor="send_newsletter" className="text-sm text-gray-300 cursor-pointer flex-1">
                      <span className="text-amber-400 font-medium">Nyhedsbrev</span> — Send påmindelser til abonnenter (3 uger, 1 uge, 3 dage før)
                    </label>
                  </div>
                )}
                
                <div className="flex gap-4 pt-4">
                  <button type="submit" disabled={savingEvent || savedEvent} className="chrome-btn px-6 py-3 rounded-lg flex-1 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="save-event-btn">
                    {savingEvent ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Gemmer...</> : savedEvent ? <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> Gemt!</> : editingEvent ? "Gem ændringer" : "Opret event"}
                  </button>
                  <button type="button" onClick={() => { setShowForm(false); setEditingEvent(null); }} className="ghost-blue-btn px-6 py-3 rounded-lg">Annuller</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarPage;
